import React, { useState, useEffect } from "react";
import { Card, Tabs, Typography, Alert } from "antd";
import {
  DollarSign,
  Building2,
  Package,
} from "lucide-react";
import {
  ServiceMetricsOverview,
  PackingMetricsTab,
  StorageBufferTab,
} from "~/components/admin/service-metrics";
import { useSearchParams } from "react-router-dom";

const { Title, Text } = Typography;

export const ServiceMetricsScreen: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") || "overview"
  );

  // Update URL when tab changes
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  // Sync URL params with tab state
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [activeTab, searchParams]);

  const tabItems = [
    {
      key: "overview",
      label: (
        <span className="flex items-center gap-2">
          <DollarSign size={16} />
          Overview
        </span>
      ),
      children: <ServiceMetricsOverview />,
    },
    {
      key: "packing-metrics",
      label: (
        <span className="flex items-center gap-2">
          <Package size={16} />
          Packing Metrics
        </span>
      ),
      children: <PackingMetricsTab />,
    },
    {
      key: "storage-buffer",
      label: (
        <span className="flex items-center gap-2">
          <Building2 size={16} />
          Storage Buffer
        </span>
      ),
      children: <StorageBufferTab />,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Title level={2} className="!mb-2">
          Service Metrics
        </Title>
        <Text type="secondary">
          Manage service metrics for different services
        </Text>
      </div>

      {/* Information Alert */}
      <Alert
        message="Service Metrics Information"
        description={
          <div className="space-y-2">
            <p>
              <strong>Collection + Delivery:</strong> Vessel price hourly +
              staff quantity × price each (standard position is art handler at
              $100 per hour), multiplied by travel time + site duration
            </p>
            <p>
              <strong>Site Service:</strong> Same as collection + delivery but
              with a minimum of $300 per art handler for first 2 hours, then
              usual hourly rate
            </p>
            <p>
              <strong>Warehouse + Fabrication:</strong> Calculated with add-on
              services based on cubic footage or hourly rates
            </p>
            <p>
              <strong>Example:</strong> Chelsea job with 2 staff ($200) and van
              ($90), travel time 0.5 hours, site time 45 minutes = 1.25 hours ×
              $290 = $362.50
            </p>
          </div>
        }
        type="info"
        showIcon
        className="mb-6"
      />

      {/* Main Content */}
      <Card className="min-h-[600px]">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          size="large"
          items={tabItems}
        />
      </Card>
    </div>
  );
};
